// MissingCharges: the cardholder's side of the No receipt pile, on /receipts.
// Each of my own card charges with no receipt gets three answers: upload the
// receipt for that exact charge, say I don't have it, or flag it SUS. Answered
// charges stay below in a quieter group so a wrong answer can be undone; the
// bookkeeper sees the answer on her board.

import React, { useEffect, useRef, useState } from 'react';
import useReceiptsStore from '../stores/useReceiptsStore';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate, businessToday, parseDate } from '../utils/date-utils';
import { formatCardLabel } from './cardLabel';
import susStamp from '../img/sus.png';
import Pager from './Pager';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Tag } from '../components/ui/Tag';
import { Panel } from '../components/ui/Panel';
import { ListRow } from '../components/ui/ListRow';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import './susStamp.css';
import type { CardholderResponseKind, MissingCharge } from './types';

// ── Formatting, shared with the upload dialog's "For the … charge" line ──────

/** The charge's magnitude: statements store purchases negative. */
export const chargeMoney = (amount: string | number) => `$${Math.abs(Number(amount)).toFixed(2)}`;

/** "Jun 12", or "Dec 30, 2025" once the charge is from another year. */
export const chargeDate = (ymd: string) => {
  const d = parseDate(ymd);
  if (!d) return '';
  return formatEventDate(ymd, d.year() === businessToday().year() ? 'MMM D' : 'MMM D, YYYY');
};

/** "For the $42.50 charge at GEAR STORE on Jun 12" */
export const chargeSentence = (c: MissingCharge) =>
  `For the ${chargeMoney(c.amount)} charge${c.description ? ` at ${c.description}` : ''} on ${chargeDate(c.txn_date)}`;

const serverMessage = (e: any, fallback: string) => e?.data?.error?.message || e?.message || fallback;

interface Props {
  /** Opens the upload dialog for this exact charge. */
  onUpload: (charge: MissingCharge) => void;
  /** An answer landed or was refused; the page refetches the list. */
  onChanged?: () => void;
  /** Rendered under a tab that already names it: no heading row of its own. */
  embedded?: boolean;
}

export default function MissingCharges({ onUpload, onChanged, embedded = false }: Props) {
  const toast = useToast();
  const charges = useReceiptsStore((s) => s.missingCharges);
  const respondToCharge = useReceiptsStore((s) => s.respondToCharge);
  const clearChargeResponse = useReceiptsStore((s) => s.clearChargeResponse);

  const [busy, setBusy] = useState<ReadonlySet<number>>(() => new Set());
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [note, setNote] = useState('');
  // Rows answered in this session land with the stamp; rows that arrived answered
  // from the server just sit there.
  const [landed, setLanded] = useState<ReadonlySet<number>>(() => new Set());
  const [announce, setAnnounce] = useState('');

  // Focus follows a row across the two lists, and returns to SUS when its note closes.
  const keys = useRef<Map<string, HTMLButtonElement>>(new Map());
  const keyRef = (kind: 'upload' | 'sus' | 'undo', id: number) => (el: HTMLButtonElement | null) => {
    if (el) keys.current.set(`${kind}:${id}`, el); else keys.current.delete(`${kind}:${id}`);
  };
  const [focusNext, setFocusNext] = useState<{ kind: 'upload' | 'sus' | 'undo'; id: number } | null>(null);
  // The key is asked for before the row's busy flag clears, and a disabled button
  // refuses focus, so the request waits (and re-runs on `busy`) until it can land.
  useEffect(() => {
    if (!focusNext) return;
    const el = keys.current.get(`${focusNext.kind}:${focusNext.id}`);
    if (!el || el.disabled) return;
    el.focus();
    setFocusNext(null);
  }, [focusNext, charges, busy]);

  // A charge with no receipt is not a thing to announce while there are none.
  if (charges.length === 0) return null;

  const open = charges.filter((c) => !c.response);
  const answered = charges.filter((c) => c.response);

  const setRowBusy = (id: number, on: boolean) => setBusy((s) => {
    const n = new Set(s);
    if (on) n.add(id); else n.delete(id);
    return n;
  });
  // Move focus only if it has not gone elsewhere since the key was pressed: still on
  // that key, or dropped to the body because the key went disabled or unmounted.
  const focusUnmoved = (pressed: Element | null) => {
    const a = document.activeElement;
    return !a || a === document.body || a === pressed;
  };
  const label = (c: MissingCharge) => c.description || 'the charge';
  // 403: not my card any more; 404: gone; 409: matched, excepted or finalized.
  const listIsStale = (e: any) => e?.status === 403 || e?.status === 404 || e?.status === 409;

  const answer = async (c: MissingCharge, kind: CardholderResponseKind, text?: string) => {
    const pressed = document.activeElement;
    setRowBusy(c.id, true);
    try {
      await respondToCharge(c.id, kind, text?.trim() || undefined);
      setLanded((s) => new Set(s).add(c.id));
      // Only this row's note closes; another row's half-typed note stays put.
      // (`note` is reset whenever a form opens, so it needs no clearing here.)
      setNoteFor((n) => (n === c.id ? null : n));
      setAnnounce(`Saved: ${label(c)}`);
      if (focusUnmoved(pressed)) setFocusNext({ kind: 'undo', id: c.id });
      onChanged?.();
    } catch (e: any) {
      toast.error(serverMessage(e, 'Could not save your answer. Try again.'));
      if (listIsStale(e)) onChanged?.();
    } finally {
      setRowBusy(c.id, false);
    }
  };

  const undo = async (c: MissingCharge) => {
    const pressed = document.activeElement;
    setRowBusy(c.id, true);
    try {
      await clearChargeResponse(c.id);
      setLanded((s) => { const n = new Set(s); n.delete(c.id); return n; });
      setAnnounce(`Undone: ${label(c)}`);
      if (focusUnmoved(pressed)) setFocusNext({ kind: 'upload', id: c.id });
      onChanged?.();
    } catch (e: any) {
      toast.error(serverMessage(e, 'Could not undo that. Try again.'));
      if (listIsStale(e)) onChanged?.();
    } finally {
      setRowBusy(c.id, false);
    }
  };

  const openNote = (c: MissingCharge) => {
    setNoteFor(c.id);
    setNote('');
  };
  const closeNote = (c: MissingCharge) => {
    setNoteFor((n) => (n === c.id ? null : n));
    setFocusNext({ kind: 'sus', id: c.id });
  };

  return (
    <Panel
      className="mb-8"
      title={embedded ? undefined : 'Charges without a receipt'}
      label={embedded ? 'Charges without a receipt' : undefined}
      actions={!embedded ? <Appear show={open.length > 0}><span className="text-xs tabular-nums text-on-surface-variant">({open.length})</span></Appear> : undefined}
    >
      <p aria-live="polite" className="sr-only">{announce}</p>
      <p className="mb-3 px-1 text-xs text-on-surface-variant">
        {open.length > 0
          ? "Your card statement shows these with no receipt. Upload one, or say why you can't."
          : 'Nothing waiting on you. Undo an answer if you got one wrong.'}
      </p>

      <Reveal show={open.length > 0}>
        <Pager items={open} label="Pages of charges without a receipt">{(page) => (
        <ul className="m-0 grid list-none gap-1.5 p-0">
          {page.map((c) => {
            const rowBusy = busy.has(c.id);
            const descId = `missing-charge-${c.id}`;
            return (
              <ListRow key={c.id}>
                <ChargeLine charge={c} descId={descId}>
                  <button type="button" ref={keyRef('upload', c.id)} onClick={() => onUpload(c)} disabled={rowBusy}
                    aria-describedby={descId} className={buttonClass('quiet', 'sm')}>
                    Upload receipt
                  </button>
                  <Tooltip label="You paid this but have no receipt. The bookkeeper takes it from here">
                    <button type="button" onClick={() => answer(c, 'no_receipt')} disabled={rowBusy}
                      aria-describedby={descId} className={buttonClass('quiet', 'sm')}>
                      I don't have it
                    </button>
                  </Tooltip>
                  <Tooltip label="You don't recognize this charge. The bookkeeper looks into it">
                    <button type="button" ref={keyRef('sus', c.id)} onClick={() => (noteFor === c.id ? setNoteFor(null) : openNote(c))} disabled={rowBusy}
                      aria-label="Flag as suspicious" aria-describedby={descId} aria-expanded={noteFor === c.id}
                      className={buttonClass('quiet', 'sm')}>
                      <img src={susStamp} alt="" draggable={false} className="sus-stamp h-5 w-auto select-none" />
                    </button>
                  </Tooltip>
                </ChargeLine>

                <Reveal show={noteFor === c.id}>
                  <div className="px-0.5 pb-0.5 pt-2.5">
                  <form
                    className="flex flex-col gap-2 sm:flex-row sm:items-end"
                    onSubmit={(e) => { e.preventDefault(); answer(c, 'suspicious', note); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); closeNote(c); } }}
                  >
                    <label className="min-w-0 flex-1">
                      <span className="mb-1 block text-xs text-on-surface-variant">
                        What looks off? (optional)
                      </span>
                      <Field autoFocus type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                        disabled={rowBusy} placeholder="I don't recognize this one" />
                    </label>
                    <div className="flex shrink-0 items-center justify-end gap-1">
                      <button type="button" onClick={() => closeNote(c)} disabled={rowBusy} className={buttonClass('quiet', 'sm')}>Cancel</button>
                      <button type="submit" disabled={rowBusy}
                        className={buttonClass('danger', 'sm')}>
                        {rowBusy ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </form>
                  </div>
                </Reveal>
              </ListRow>
            );
          })}
        </ul>
        )}</Pager>
      </Reveal>

      <Reveal show={answered.length > 0}>
        <div className={open.length > 0 ? 'pt-4' : ''}>
          <h3 className="mb-1.5 px-1 text-xs font-semibold text-on-surface-variant">
            Answered <span className="font-normal tabular-nums">({answered.length})</span>
          </h3>
          <Pager items={answered} label="Pages of answered charges">{(page) => (
          <ul className="m-0 grid list-none gap-1 p-0">
            {page.map((c) => {
              const r = c.response!;
              const isNew = landed.has(c.id);
              const descId = `missing-charge-${c.id}`;
              return (
                <ListRow key={c.id}
                  className={isNew && r.kind === 'no_receipt' ? 'animate-rowLand motion-reduce:animate-none' : ''}>
                  <ChargeLine charge={c} descId={descId} muted>
                    {r.kind === 'suspicious' ? (
                      <span className="inline-flex items-center gap-1.5">
                        <img src={susStamp} alt="" draggable={false}
                          className={`sus-stamp h-5 w-auto select-none ${isNew ? 'sus-stamp-land' : ''}`} />
                        <Tag tone="bad">Flagged</Tag>
                      </span>
                    ) : (
                      <Tag>You don't have it</Tag>
                    )}
                    <button type="button" ref={keyRef('undo', c.id)} onClick={() => undo(c)} disabled={busy.has(c.id)}
                      aria-describedby={descId} className={buttonClass('quiet', 'sm')}>
                      Undo
                    </button>
                  </ChargeLine>
                  <Reveal show={!!r.note}>
                    <p className="break-words pt-1 text-xs text-on-surface-variant">Your note: {r.note}</p>
                  </Reveal>
                </ListRow>
              );
            })}
          </ul>
          )}</Pager>
        </div>
      </Reveal>
    </Panel>
  );
}

// One charge as a statement line: what and where on the left, the amount, then the
// row's own controls. On a phone the controls drop under the line, still right-aligned
// beside the amount they act on.
function ChargeLine({ charge: c, descId, muted, children }: {
  charge: MissingCharge; descId: string; muted?: boolean; children: React.ReactNode;
}) {
  const desc = c.description || 'No description';
  return (
    // Fixed amount and action columns, so amounts line up down both lists; the action
    // column may still grow past its floor if a row's keys need more room.
    <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_6rem_minmax(17rem,auto)]">
      <div className="min-w-0">
        <div id={descId} title={desc}
          className={`truncate text-sm font-medium ${muted ? 'text-on-surface-variant' : 'text-on-surface'}`}>
          {desc}
        </div>
        <div className="mt-0.5 flex min-w-0 flex-wrap gap-x-3 text-xs text-on-surface-variant">
          <span className="whitespace-nowrap tabular-nums">{chargeDate(c.txn_date)}</span>
          <span className="min-w-0 truncate">{formatCardLabel({ label: c.card_label, last4: c.card_last4 })}</span>
          {c.period_label && <span className="whitespace-nowrap">Statement {c.period_label}</span>}
        </div>
      </div>
      <div className={`w-24 text-right font-mono text-sm tabular-nums ${muted ? 'text-on-surface-variant' : 'text-on-surface'}`}>
        {chargeMoney(c.amount)}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-1">{children}</div>
    </div>
  );
}
